import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";
import Head from "next/head";
import Link from "next/link";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

cloud.title = "Cloud Consulting Services — AWS, Azure, GCP for Enterprise India | ITMTB";
cloud.desc =
  "ITMTB architects and manages production cloud on AWS, Azure, and GCP — specialising in infrastructure for AI agent deployments, cloud migrations, and data platform engineering for Indian enterprises.";
cloud.header_img = "https://www.itmtb.com/services/capability/cloud.webp";
cloud.keywords =
  "cloud infrastructure for AI workloads India, AWS Azure GCP consulting India, cloud migration consulting India enterprise, production cloud AI deployment India, cloud cost optimisation India";

const faqItems = [
  {
    q: "Which cloud provider is best for AI workloads in India — AWS, Azure, or GCP?",
    a: "The right answer depends on your existing stack, AI use case, and compliance requirements. AWS has the broadest service coverage and competitive compute costs in the ap-south-1 (Mumbai) region — the default starting point for most new deployments. Azure is the right call if your organisation already uses Microsoft Dynamics 365, Office 365, or Azure AD: managed identity integration across services significantly reduces credential management complexity. GCP's Vertex AI and BigQuery are the strongest managed offerings for teams building heavily on Google's AI infrastructure. For data residency: all three providers have India regions (AWS ap-south-1, Azure Central India, GCP asia-south1). For DPDP compliance, the provider is less important than configuration — we've delivered compliant deployments on all three. We help you evaluate during the discovery sprint rather than picking based on vendor marketing.",
  },
  {
    q: "What does a cloud migration actually involve — how long does it take?",
    a: "A cloud migration has four phases. Assessment (2 weeks, fixed price): we inventory your current infrastructure, map dependencies, identify migration risks, and produce a migration runbook with a timeline and cost estimate. Pilot migration (4–6 weeks): we migrate one non-critical workload to validate the runbook, test backup and rollback procedures, and find infrastructure gaps before the bulk migration. Bulk migration (8–20 weeks depending on number of services and whether re-architecture is needed alongside migration). Post-migration stabilisation (4 weeks): monitoring, cost right-sizing, and performance tuning. Total calendar time typically runs 16–28 weeks for a mid-size enterprise migrating 20–50 services. The biggest variable is organisational: how quickly your application teams can validate migrated workloads.",
  },
  {
    q: "What does production cloud infrastructure for AI agents actually require?",
    a: "AI agents and ML pipelines have different infrastructure requirements than web applications. Key components: inference compute — GPU instances or managed inference endpoints (AWS SageMaker, Azure AI Foundry, Vertex AI) sized for your model and latency requirements; vector database — for RAG retrieval, options include Pinecone, Weaviate, pgvector on RDS, or managed equivalents on each cloud; high-throughput message queues — for agent event loops and async tool call processing (SQS, Azure Service Bus, Pub/Sub); cost guardrails — inference spend can spike unpredictably at scale, requiring budget alerts and automatic endpoint scaling-to-zero when idle; and networking — agents calling internal systems need private connectivity (VPC endpoints, private link) rather than public internet routes. We architect these components during the discovery sprint, selecting managed services where they reduce operational burden.",
  },
  {
    q: "How do you handle data residency requirements in Indian cloud deployments?",
    a: "Data residency — keeping specific data categories within Indian borders — is required by several regulations: RBI mandates that payment system data be stored in India; SEBI has data localisation requirements for market intermediaries; IRDAI requires insurance data in India. On AWS, Azure, and GCP, data residency is achieved through region selection and service configuration. Cloud services are global by default — some services (CDN, DNS, global load balancers) pass data through international infrastructure even when primary compute is in India. We audit your data flows, identify which services require region-pinning and which cross-region services need alternatives, and implement the controls required for your specific regulatory obligations.",
  },
  {
    q: "What is cloud cost optimisation and how much can I expect to save?",
    a: "Cloud cost optimisation is the process of identifying and eliminating unnecessary spend without reducing capability. Common sources of overspend: compute instances sized for peak load that run continuously at 15–20% utilisation (typical savings from right-sizing: 25–40%); storage accumulating without lifecycle policies moving aged data to lower-cost tiers; staging and development environments running 24×7 at production scale; AI inference endpoints staying warm when idle; and reserved instances purchased for workloads that have since changed. Organisations that haven't conducted a cost review in the past 12 months typically see 20–35% savings. The review takes 2–3 weeks and produces a prioritised list of changes with projected savings per item — you can implement selectively based on engineering capacity.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
cloud.faq = faqItems;

export default function cloud() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let cloudInsights = insights.filter(item => item?.meta?.refer?.includes("Cloud"));
  let cloudCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("Cloud"));

  if (cloudInsights.length === 1) {
    insights = [...cloudInsights, ...getRandom(insights.filter(item => !cloudInsights.includes(item)), 1)];
  } else if (cloudInsights.length >= 2) {
    insights = getRandom(cloudInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (cloudCaseStudies.length === 1) {
    caseStudies = [...cloudCaseStudies, ...getRandom(caseStudies.filter(item => !cloudCaseStudies.includes(item)), 1)];
  } else if (cloudCaseStudies.length >= 2) {
    caseStudies = getRandom(cloudCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  const subServices = [
    {
      img: null,
      key: "Production Cloud for AI Workloads",
      title: "AI agents run on cloud. We architect and manage the layer they depend on.",
      text: "AI agents and ML pipelines need cloud infrastructure designed for them: GPU inference compute, vector databases for RAG retrieval, high-throughput message queues for agent event loops, and cost guardrails on inference spend that can spike unpredictably at scale.\n\nWe architect these components from first principles — selecting the right instance types on AWS, Azure, or GCP, configuring managed inference endpoints, setting up auto-scaling and cost alerts, and designing private networking so your agents can reach internal systems without public internet exposure.",
    },
    {
      img: null,
      key: "Cloud Architecture and Infrastructure Design",
      title: "Getting your cloud architecture right from the start avoids the two most expensive outcomes: over-provisioned and under-provisioned.",
      text: "We design cloud architectures on AWS, Azure, and GCP tailored to your workload profile — multi-region resilience where required, auto-scaling for variable traffic, and cost optimisation built into the design rather than retrofitted after the bill arrives.\n\nFor enterprises with existing infrastructure: we audit the current state and produce a roadmap to the target architecture. For greenfield deployments: we select services, size infrastructure, and document the architecture decisions so your team can operate and extend it.",
    },
    {
      img: null,
      key: "Cloud Migration",
      title: "We migrate on-premises systems and between cloud providers — with a runbook your team can follow and roll back from.",
      text: "We start with a migration assessment: inventory of what's running where, dependencies, and migration risk per service. Then we design the migration path — lift-and-shift where cost or time dictates, re-architecture where the current design wouldn't scale in cloud.\n\nWe've executed migrations from on-premises Windows Server to Azure, from AWS to GCP for cost reasons, and from legacy hosting to AWS for Indian e-commerce platforms scaling to meet demand. Migration execution follows a defined runbook so your team is never surprised.",
    },
    {
      img: null,
      key: "Data Platform and Pipeline Engineering",
      title: "Your AI agents are only as good as the data they can reach — we build the data layer.",
      text: "We build data platforms that connect your operational systems — ERPs, CRMs, transaction databases, file stores — into a clean, structured layer that AI agents and analytics tools can query reliably.\n\nThis includes ingestion pipelines (real-time and batch), transformation layers that normalise data from disparate sources, data quality monitoring, and cost-efficient storage tiering on AWS S3, Azure Data Lake, or GCP Cloud Storage. For enterprises with existing data warehouses (Snowflake, Redshift, BigQuery), we extend rather than replace.",
    },
    {
      img: null,
      key: "Cloud Cost Optimisation",
      title: "Cloud bills grow faster than usage when architecture decisions made under deadline pressure aren't revisited.",
      text: "Common leaks we find: oversized compute reserved instances that no longer match actual workload; S3/GCS storage accumulating without lifecycle policies; staging environments running 24×7; AI inference endpoints staying warm when idle. We conduct a cloud cost review, produce a prioritised list of savings opportunities with projected impact per item, and implement the changes. Typical outcome: 20–35% cost reduction without any reduction in capability.",
    },
  ];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              name: "Cloud Consulting Services — AWS, Azure, GCP for Enterprise India",
              url: "https://www.itmtb.com/services/cloud",
              image: "https://www.itmtb.com/services/capability/cloud.webp",
              description:
                "ITMTB architects and manages production cloud on AWS, Azure, and GCP — specialising in infrastructure for AI agent deployments, cloud migrations, and data platform engineering for Indian enterprises.",
              provider: {
                "@type": "Organization",
                name: "ITMTB Technologies",
                url: "https://www.itmtb.com",
                logo: "https://www.itmtb.com/logo.webp",
              },
              areaServed: [{ "@type": "Place", name: "India" }],
              serviceType: [
                "Cloud Infrastructure for AI Workloads",
                "AWS Consulting India",
                "Azure Consulting India",
                "GCP Consulting India",
                "Cloud Migration Consulting",
                "Production Cloud AI Deployment",
                "Cloud Cost Optimisation",
                "Data Platform Engineering",
              ],
              sameAs: [
                "https://www.linkedin.com/company/itmtb-technologies/",
                "https://x.com/ITMTBTech",
                "https://www.instagram.com/itmtbtech/",
              ],
            }),
          }}
        />
      </Head>

      <Service2
        heading="Production Cloud for Enterprise AI"
        paragraph={`Production AI systems don't run on laptops. They need cloud infrastructure designed for the job: GPU inference compute, vector databases for retrieval, high-throughput queues for agent event loops, and data residency controls for Indian regulatory compliance.

ITMTB architects, migrates, and manages cloud infrastructure on AWS, Azure, and GCP — with a specialist focus on the infrastructure that production AI systems require. We rebuilt a cloud stack that handled 3× peak traffic without degradation, delivering for a D2C brand scaling its digital operations in 4 months.

The cloud layer is where AI projects fail silently — cost overruns from idle inference endpoints, cold-start latency, misconfigured IAM, broken observability. We make sure yours doesn't.`}
        bgPath="/services/capability/cloud.webp"
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag="Cloud"
        maxItems={3}
        heading="Selected work in cloud"
      />

      <CustomerSection title="Trusted by" />

      <MethodologyAnchor />

      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
          Cloud is the layer our software runs on — it sits within our broader{" "}
          <Link href="/services/custom-software-development">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">custom software development</a>
          </Link>{" "}
          practice, alongside platform engineering, application development, and AI-enabled systems. For buyers comparing providers or estimating spend, our{" "}
          <Link href="/blog/cloud-price-comparison">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">cloud cost comparison guide</a>
          </Link>{" "}
          explains how to model public cloud cost by workload, usage, and cost allocation, and our{" "}
          <Link href="/blog/aws-cost-optimization-unused-resources">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">AWS cost optimisation guide</a>
          </Link>{" "}
          covers how to identify idle and oversized AWS resources and validate them before anything is removed. When the decision involves a captive or hybrid facility rather than public cloud, our{" "}
          <Link href="/services/data-center-project-readiness-governance">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">data centre project readiness and delivery governance</a>
          </Link>{" "}
          service helps test assumptions and govern the setup before major commitments.
        </p>
      </section>

      <ServiceFAQ items={faqItems} />

      <ServiceEngagementLinks />

      <ContactUsHome />

      <Footer />
    </div>
  );
}
